import { apiService } from "api/apiService";
import type {
  StudentProfileResource,
  AssessmentModelResource,
  EducationLevelResource,
  PublicPortfolioSubjectResource,
} from "api/generated";
import {
  portfolioProfileFromDto,
  educationLevelFromDto,
  assessmentModelFromDto,
  subjectFromDto,
} from "portfolio/api/mappers";
import type {
  PortfolioProfile,
  PortfolioEducationLevel,
  PortfolioAssessmentModel,
  PortfolioSubject,
} from "portfolio/types";

// The stakeholder option fetchers moved to the shared student selection
// layer; this one is re-exported for teacherEducationLevelStore.
export { fetchEducationLevels } from "shared/api";

export const fetchPortfolioProfile = async (
  id: string,
): Promise<PortfolioProfile> => {
  if (!id) {
    throw new Error("Student ID is required to fetch portfolio profile");
  }

  const {
    data: { data: profile },
  } = await apiService.get<{ data: StudentProfileResource }>(
    `/api/portfolio/students/${id}/profile`,
  );

  return portfolioProfileFromDto(profile);
};

export const fetchAssessmentModels = async (): Promise<
  PortfolioAssessmentModel[]
> => {
  const {
    data: { data: models },
  } = await apiService.get<{ data: AssessmentModelResource[] }>(
    "api/portfolio/assessment-models",
  );

  return models.map(assessmentModelFromDto);
};

export const fetchSubjects = async (): Promise<PortfolioSubject[]> => {
  const {
    data: { data: subjects },
  } = await apiService.get<{ data: PublicPortfolioSubjectResource[] }>(
    "/api/public-portfolio-subjects",
  );

  return subjects.map(subjectFromDto);
};

/**
 * Fetches all education levels for library task categorization.
 * This is different from fetchEducationLevels which is for stakeholder selection.
 */
export const fetchAllEducationLevels = async (): Promise<
  PortfolioEducationLevel[]
> => {
  const {
    data: { data: educationLevels },
  } = await apiService.get<{ data: EducationLevelResource[] }>(
    "api/portfolio/all-education-levels",
  );

  return educationLevels.map(educationLevelFromDto);
};
